import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { useState } from 'react';
import TicketedEventCategoriesList from '@Components/TicketedEventCategories/List';
import TicketedEventCategoryForm from '@Components/TicketedEventCategories/Form';
import Breadcrumbs from '@Components/Breadcrumbs';
import Heading from '@Components/Heading';
import type { TicketedEventCategory } from '@Types/ticketedEvents';

const CREATE_FORM_ID = 'create-category-form';
const EDIT_FORM_ID = 'edit-category-form';

export default function EventCategories() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editCategory, setEditCategory] =
    useState<TicketedEventCategory | null>(null);

  const handleRowClick = (row: { original: TicketedEventCategory }) => {
    setEditCategory(row.original);
    setEditOpen(true);
  };

  return (
    <div>
      <Breadcrumbs
        navList={[
          { name: 'Events', href: '/events' },
          { name: 'Categories', href: '/events/categories' },
        ]}
      />
      <div className="mt-8 mb-4 flex items-center justify-between">
        <div>
          <Heading>Categories</Heading>
        </div>
        <Sheet open={createOpen} onOpenChange={setCreateOpen}>
          <SheetTrigger asChild>
            <Button variant="primary">Create new category</Button>
          </SheetTrigger>

          <SheetContent side="right" className="w-full sm:max-w-xl">
            <SheetHeader>
              <SheetTitle>Create category</SheetTitle>
              <SheetDescription>
                Add a new category with a name and description.
              </SheetDescription>
            </SheetHeader>

            <SheetBody className="grow">
              <TicketedEventCategoryForm
                onSuccess={() => setCreateOpen(false)}
                submitLabel="Create"
                formId={CREATE_FORM_ID}
                hideInternalActions
              />
            </SheetBody>

            <SheetFooter className="mt-4">
              <Button type="submit" form={CREATE_FORM_ID} variant="primary">
                Create
              </Button>
              <SheetClose asChild>
                <Button variant="secondary" type="button">
                  Close
                </Button>
              </SheetClose>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      <TicketedEventCategoriesList onRowClick={handleRowClick} />

      {/* Edit Sheet */}
      <Sheet
        open={editOpen}
        onOpenChange={(isOpen) => {
          setEditOpen(isOpen);
          if (!isOpen) setEditCategory(null);
        }}
      >
        <SheetContent side="right" className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Edit category</SheetTitle>
            <SheetDescription>
              Update the category name and description.
            </SheetDescription>
          </SheetHeader>

          <SheetBody className="grow">
            {editCategory && (
              <TicketedEventCategoryForm
                key={editCategory.id}
                editId={editCategory.id}
                defaultValues={{
                  name: editCategory.name,
                  description: editCategory.description ?? '',
                }}
                onSuccess={() => {
                  setEditOpen(false);
                  setEditCategory(null);
                }}
                submitLabel="Update"
                formId={EDIT_FORM_ID}
                hideInternalActions
              />
            )}
          </SheetBody>

          <SheetFooter className="mt-4">
            <Button type="submit" form={EDIT_FORM_ID} variant="primary">
              Update
            </Button>
            <SheetClose asChild>
              <Button variant="secondary" type="button">
                Close
              </Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
